import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useTranslation } from "react-i18next";
import { Company } from "../types";

interface CompanyEditFormProps {
  company: Company | null;
  _onSave: (company: Company) => void;
}

export interface CompanyEditFormRef {
  getFormData: () => Company;
}

const CompanyEditForm = forwardRef<CompanyEditFormRef, CompanyEditFormProps>(
  ({ company, _onSave }, ref) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState<Company>({
      company_id: "",
      name: "",
      description: "",
      contact_name: "",
      contact_email: "",
      contact_phone: "",
      website: "",
      logo: "",
      address: undefined, // Don't initialize with empty object
      service_areas: undefined, // Don't initialize with empty array
      business_hours: undefined, // Don't initialize with empty object
    });

    useEffect(() => {
      if (company) {
        // Parse JSON strings back to objects if needed
        let address = company.address;
        let service_areas = company.service_areas;
        let business_hours = company.business_hours;

        // Handle address parsing
        if (typeof company.address === "string") {
          try {
            address = JSON.parse(company.address);
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.warn(
                "Failed to parse address JSON in form:",
                company.address
              );
            }
            address = undefined;
          }
        }

        // Handle service_areas parsing
        if (typeof company.service_areas === "string") {
          try {
            service_areas = JSON.parse(company.service_areas);
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.warn(
                "Failed to parse service_areas JSON in form:",
                company.service_areas
              );
            }
            service_areas = undefined;
          }
        }

        // Handle business_hours parsing
        if (typeof company.business_hours === "string") {
          try {
            business_hours = JSON.parse(company.business_hours);
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.warn(
                "Failed to parse business_hours JSON in form:",
                company.business_hours
              );
            }
            business_hours = undefined;
          }
        }

        setFormData({
          ...company,
          address: address,
          service_areas: service_areas,
          business_hours: business_hours,
        });
      }
    }, [company]);

    // Expose the getFormData method to the parent component
    useImperativeHandle(ref, () => ({
      getFormData: () => formData,
    }));

    const handleInputChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      try {
        const { name, value } = e.target;
        setFormData((prevState) => ({
          ...prevState,
          [name]: value,
        }));
      } catch (error) {
        // Suppress browser extension errors during form interactions
        if (process.env.NODE_ENV === "development") {
          console.debug("Form interaction error suppressed:", error);
        }
      }
    };

    const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      try {
        const { name, value } = e.target;
        setFormData((prevState) => ({
          ...prevState,
          address: {
            ...prevState.address,
            [name]: value,
          },
        }));
      } catch (error) {
        // Suppress browser extension errors during form interactions
        if (process.env.NODE_ENV === "development") {
          console.debug("Address form interaction error suppressed:", error);
        }
      }
    };

    const handleServiceAreaChange = (index: number, value: string) => {
      try {
        setFormData((prevState) => {
          const newServiceAreas = [...(prevState.service_areas || [])];
          newServiceAreas[index] = value;
          return {
            ...prevState,
            service_areas: newServiceAreas,
          };
        });
      } catch (error) {
        // Suppress browser extension errors during form interactions
        if (process.env.NODE_ENV === "development") {
          console.debug("Service area interaction error suppressed:", error);
        }
      }
    };

    const addServiceArea = () => {
      try {
        setFormData((prevState) => ({
          ...prevState,
          service_areas: [...(prevState.service_areas || []), ""],
        }));
      } catch (error) {
        // Suppress browser extension errors during form interactions
        if (process.env.NODE_ENV === "development") {
          console.debug("Add service area error suppressed:", error);
        }
      }
    };

    const removeServiceArea = (index: number) => {
      try {
        setFormData((prevState) => {
          const newServiceAreas = [...(prevState.service_areas || [])];
          newServiceAreas.splice(index, 1);
          return {
            ...prevState,
            service_areas: newServiceAreas,
          };
        });
      } catch (error) {
        // Suppress browser extension errors during form interactions
        if (process.env.NODE_ENV === "development") {
          console.debug("Remove service area error suppressed:", error);
        }
      }
    };

    const fillWithDemoData = () => {
      try {
        const demoOptions = [
          {
            name: "Green Thumb Landscaping LLC",
            description:
              "Professional landscaping and garden design services. We specialize in residential and commercial property maintenance, lawn care, and outdoor living spaces.",
            contact_name: "Sarah Johnson",
            contact_email: "sarah@greenthumblandscaping.com",
            contact_phone: "(555) 123-4567",
            website: "https://www.greenthumblandscaping.com",
            address: {
              street: "123 Garden Street",
              city: "Springfield",
              state: "IL",
              zip_code: "62701",
              country: "USA",
            },
            service_areas: [
              "Springfield",
              "Champaign",
              "Urbana",
              "Decatur",
              "Bloomington",
            ],
          },
          {
            name: "Perfect Lawns Inc.",
            description:
              "Comprehensive lawn care and maintenance company serving residential and commercial clients. Expert lawn mowing, fertilization, weed control, and seasonal treatments.",
            contact_name: "Mike Chen",
            contact_email: "mike@perfectlawns.com",
            contact_phone: "(555) 987-6543",
            website: "https://www.perfectlawns.com",
            address: {
              street: "456 Meadow Lane",
              city: "Riverside",
              state: "CA",
              zip_code: "92501",
              country: "USA",
            },
            service_areas: [
              "Riverside",
              "Corona",
              "Moreno Valley",
              "Temecula",
              "Murrieta",
            ],
          },
          {
            name: "Urban Gardens Co.",
            description:
              "Specialized in urban gardening solutions, rooftop gardens, vertical farming, and sustainable green spaces for city environments.",
            contact_name: "Dr. Emily Rodriguez",
            contact_email: "emily@urbangardens.co",
            contact_phone: "(555) 456-7890",
            website: "https://www.urbangardens.co",
            address: {
              street: "789 City Center Blvd",
              city: "Metropolis",
              state: "NY",
              zip_code: "10001",
              country: "USA",
            },
            service_areas: [
              "Manhattan",
              "Brooklyn",
              "Queens",
              "Bronx",
              "Staten Island",
            ],
          },
        ];

        // Cycle through demo options
        const currentName = formData.name || "";
        const currentIndex = demoOptions.findIndex(
          (option) => option.name === currentName
        );
        const nextIndex = (currentIndex + 1) % demoOptions.length;
        const selectedDemo = demoOptions[nextIndex];

        const demoData: Company = {
          company_id: "",
          name: selectedDemo.name,
          description: selectedDemo.description,
          contact_name: selectedDemo.contact_name,
          contact_email: selectedDemo.contact_email,
          contact_phone: selectedDemo.contact_phone,
          website: selectedDemo.website,
          logo: `https://example.com/logos/${selectedDemo.name
            .toLowerCase()
            .replace(/\s+/g, "")
            .replace(/[^a-z0-9]/gi, "")}.png`,
          address: selectedDemo.address,
          service_areas: selectedDemo.service_areas,
          business_hours: {
            monday: "8:00 AM - 6:00 PM",
            tuesday: "8:00 AM - 6:00 PM",
            wednesday: "8:00 AM - 6:00 PM",
            thursday: "8:00 AM - 6:00 PM",
            friday: "8:00 AM - 6:00 PM",
            saturday: "9:00 AM - 4:00 PM",
            sunday: "Closed",
          },
          timestamps: {},
        };

        setFormData(demoData);
      } catch (error) {
        // Suppress browser extension errors during demo data loading
        if (process.env.NODE_ENV === "development") {
          console.debug("Demo data loading error suppressed:", error);
        }
      }
    };

    return (
      <div
        className="company-edit-form"
        style={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        {/* Quick Fill Button - Only show in development */}
        {import.meta.env.VITE_DEV_AUTH_BYPASS && (
          <div className="mb-3">
            <button
              type="button"
              className="btn btn-outline-info btn-sm"
              onClick={fillWithDemoData}
              title="Cycle through different demo company data for testing"
            >
              🚀 Demo Data ({formData.name ? "Next" : "Load"})
            </button>
          </div>
        )}

        {/* Basic Information */}
        <div className="row">
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label htmlFor="name">{t("forms.labels.companyName *")}</label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                value={formData.name || ""}
                onChange={handleInputChange}
                required
                placeholder="Enter company name"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label htmlFor="contact_name">
                {t("forms.labels.contactPerson")}
              </label>
              <input
                type="text"
                className="form-control"
                id="contact_name"
                name="contact_name"
                value={formData.contact_name || ""}
                onChange={handleInputChange}
                placeholder="Primary contact person"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="row">
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label htmlFor="contact_email">Contact Email</label>
              <input
                type="email"
                className="form-control"
                id="contact_email"
                name="contact_email"
                value={formData.contact_email || ""}
                onChange={handleInputChange}
                placeholder="contact@company.com"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label htmlFor="contact_phone">Contact Phone</label>
              <input
                type="tel"
                className="form-control"
                id="contact_phone"
                name="contact_phone"
                value={formData.contact_phone || ""}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
        </div>

        {/* Company Details */}
        <div className="row">
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label htmlFor="website">Website</label>
              <input
                type="url"
                className="form-control"
                id="website"
                name="website"
                value={formData.website || ""}
                onChange={handleInputChange}
                placeholder="https://www.company.com"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label htmlFor="logo">Logo URL</label>
              <input
                type="url"
                className="form-control"
                id="logo"
                name="logo"
                value={formData.logo || ""}
                onChange={handleInputChange}
                placeholder="https://example.com/logo.png"
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="form-group mb-3">
          <label htmlFor="description">Description</label>
          <textarea
            className="form-control"
            id="description"
            name="description"
            value={formData.description || ""}
            onChange={handleInputChange}
            rows={3}
            placeholder="Brief description of the company"
          />
        </div>

        {/* Address */}
        <h6 className="mt-4 mb-3">Address</h6>
        <div className="row">
          <div className="col-12">
            <div className="form-group mb-3">
              <label htmlFor="street">Street Address</label>
              <input
                type="text"
                className="form-control"
                id="street"
                name="street"
                value={formData.address?.street || ""}
                onChange={handleAddressChange}
                placeholder="123 Main Street"
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4">
            <div className="form-group mb-3">
              <label htmlFor="city">City</label>
              <input
                type="text"
                className="form-control"
                id="city"
                name="city"
                value={formData.address?.city || ""}
                onChange={handleAddressChange}
                placeholder="City"
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group mb-3">
              <label htmlFor="state">State/Province</label>
              <input
                type="text"
                className="form-control"
                id="state"
                name="state"
                value={formData.address?.state || ""}
                onChange={handleAddressChange}
                placeholder="State"
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group mb-3">
              <label htmlFor="zip_code">ZIP/Postal Code</label>
              <input
                type="text"
                className="form-control"
                id="zip_code"
                name="zip_code"
                value={formData.address?.zip_code || ""}
                onChange={handleAddressChange}
                placeholder="12345"
              />
            </div>
          </div>
        </div>

        {/* Service Areas */}
        <div className="form-group mb-3">
          <label>Service Areas</label>
          {formData.service_areas?.map((area, index) => (
            <div key={index} className="d-flex mb-2">
              <input
                type="text"
                className="form-control me-2"
                value={area}
                onChange={(e) => handleServiceAreaChange(index, e.target.value)}
                placeholder="Service area"
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => removeServiceArea(index)}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={addServiceArea}
          >
            Add Service Area
          </button>
        </div>

        {/* Backward compatibility - keep old email field */}
        <div className="form-group mb-3" style={{ display: "none" }}>
          <label htmlFor="email">{t("forms.labels.email (Legacy)")}</label>
          <input
            type="email"
            className="form-control"
            id="email"
            name="email"
            value={formData.contact_email || ""}
            onChange={handleInputChange}
          />
        </div>
      </div>
    );
  }
);

CompanyEditForm.displayName = "CompanyEditForm";

export default CompanyEditForm;
